/**
 * Product Field Registry
 * Single source of truth for all Product field definitions
 * Used by adapters to generate DataGrid columns, Advanced Search configs, and Modal fields
 */

import { Product } from '../../services/api/domains/products/types';
import { BaseFieldDefinition } from '../../shared/types/fieldRegistry';
import { createFieldHelpers } from '../../shared/utils/fieldRegistryHelpers';

/**
 * Complete Product Field Registry
 * Order matches typical display priority
 *
 * NOTE: The following fields are intentionally excluded:
 * - product_id: Auto-generated, handled separately by components
 * - img: Not currently used in UI, may be added later
 */
export const PRODUCT_FIELDS_REGISTRY: BaseFieldDefinition[] = [
  // ========== Core Product Information ==========
  {
    key: 'product_name',
    label: 'Official Product Name',
    dataType: 'string',
    isRequired: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    validation: { maxLength: 450 },
    renderConfig: {
      width: 250,
      flex: 1,
      maxDisplayLength: 100,
      formatAs: 'html',
    },
  },
  {
    key: 'preferred_product_name',
    label: 'Product Name',
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true, // NEW: Added to advanced search
    description:
      'Preferred common name for internal reference. Select from synonyms or enter custom name.',
    renderConfig: {
      width: 250,
      maxDisplayLength: 100,
      formatAs: 'html',
    },
  },
  {
    key: 'cas_number',
    label: 'CAS #',
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    description: 'Chemical Abstracts Service registry number',
    renderConfig: { width: 150 },
  },

  // ========== Relational Fields ==========
  {
    key: 'grades',
    label: 'Product Type',
    dataType: 'array',
    primitiveType: 'object',
    isRelation: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    relationConfig: {
      type: 'many-to-many',
      entityName: 'grades',
      idField: 'grade_id',
      displayField: 'grade_name',
      searchField: 'grade_name',
      dataSource: 'api',
      apiEndpoint: '/grades',
    },
    renderConfig: {
      width: 150,
      maxDisplayItems: 2,
    },
  },
  {
    key: 'therapeutic_areas',
    label: 'Therapeutic Area',
    dataType: 'array',
    primitiveType: 'object',
    isRelation: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    relationConfig: {
      type: 'many-to-many',
      entityName: 'therapeutic_areas',
      idField: 'therapeutic_area_id',
      displayField: 'therapeutic_area_name',
      searchField: 'therapeutic_area_name',
      dataSource: 'api',
      apiEndpoint: '/therapeutic_areas',
    },
    renderConfig: {
      width: 200,
      maxDisplayItems: 2,
    },
  },
  {
    key: 'manufacturers',
    label: 'Associated Manufacturers',
    dataType: 'array',
    primitiveType: 'object',
    isRelation: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    relationConfig: {
      type: 'many-to-many',
      entityName: 'manufacturers',
      idField: 'manufacturer_id',
      displayField: 'manufacturer_name',
      searchField: 'manufacturer_name',
      statusField: 'manufacturing_status',
      dataSource: 'api',
      apiEndpoint: '/manufacturers',
    },
    selectionOptions: [
      { value: 'Commercial', label: 'Commercial' },
      { value: 'R&D', label: 'R&D' },
    ],
    renderConfig: {
      width: 300,
      flex: 1,
      maxDisplayItems: 2,
    },
  },
  {
    key: 'facilities',
    label: 'Facilities',
    dataType: 'array',
    primitiveType: 'object',
    isRelation: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    relationConfig: {
      type: 'many-to-many',
      entityName: 'facilities',
      idField: 'facility_id',
      displayField: 'facility_name',
      searchField: 'facility_name',
      dataSource: 'api',
      apiEndpoint: '/facilities',
    },
    renderConfig: {
      width: 300,
      maxDisplayItems: 2,
    },
  },

  // ========== Chemical Properties ==========
  {
    key: 'molecular_formula',
    label: 'Molecular Formula',
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 180,
      maxDisplayLength: 100,
      formatAs: 'html',
    },
  },
  {
    key: 'molecular_mass',
    label: 'Molecular Mass',
    dataType: 'string', // Keep as string per user requirement
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: { width: 150 },
  },
  {
    key: 'is_cas_verified',
    label: 'CAS Verified',
    dataType: 'boolean',
    isReadOnly: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    description: 'Indicates whether the CAS number has been verified',
    selectionOptions: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
    renderConfig: { width: 120 },
  },
  {
    key: 'uri',
    label: 'URI',
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: { width: 200 },
  },
  {
    key: 'inchi',
    label: 'InChI',
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 250,
      maxDisplayLength: 100,
    },
  },
  {
    key: 'inchi_key',
    label: 'InChI Key',
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: false, // Not currently in advanced search
    renderConfig: { width: 200 },
  },
  {
    key: 'smile',
    label: 'SMILES',
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 250,
      maxDisplayLength: 100,
    },
  },
  {
    key: 'canonical_smile',
    label: 'Canonical SMILES',
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 250,
      maxDisplayLength: 100,
    },
  },

  // ========== Additional Product Data ==========
  {
    key: 'synonyms',
    label: 'Synonyms',
    dataType: 'array',
    primitiveType: 'string',
    isSortable: false,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 300,
      maxDisplayItems: 3,
    },
  },
  {
    key: 'replaced_rns',
    label: 'Replaced RNs',
    dataType: 'array',
    primitiveType: 'string',
    isSortable: false,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 250,
      maxDisplayItems: 3,
    },
  },
  {
    key: 'note',
    label: 'Note',
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 250,
      maxDisplayLength: 100,
      rows: 4,
    },
  },

  // ========== Metadata Fields ==========
  {
    key: 'user_id',
    label: 'Created By',
    dataType: 'string',
    isReadOnly: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: { width: 200 },
  },
  {
    key: 'created_date',
    label: 'Created Date',
    dataType: 'date',
    isReadOnly: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 180,
      formatAs: 'datetime',
    },
  },
  {
    key: 'modified_date',
    label: 'Modified Date',
    dataType: 'date',
    isReadOnly: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 180,
      formatAs: 'datetime',
    },
  },
];

// Create field helpers using shared utility
const helpers = createFieldHelpers<Product>(PRODUCT_FIELDS_REGISTRY);

export const getFieldByKey = helpers.getFieldByKey;
export const getSearchableFields = helpers.getSearchableFields;
export const getFilterableFields = helpers.getFilterableFields;
export const getSortableFields = helpers.getSortableFields;
export const getEditableFields = helpers.getEditableFields;
export const getRelationFields = helpers.getRelationFields;
export const getComputedFields = helpers.getComputedFields;
export const getReadOnlyFields = helpers.getReadOnlyFields;
export const getRequiredFields = helpers.getRequiredFields;
